import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LexPathAdapter } from "./lib/paths/lex";
import { PipecatPathAdapter } from "./lib/paths/pipecat";
import { NovaSonicPathAdapter } from "./lib/paths/nova-sonic";
import { PATHS, type Message, type Metric, type PathAdapter, type PathId } from "./lib/types";

const SCENARIOS: { label: string; text: string }[] = [
  { label: "Balance check", text: "What is my checking balance?" },
  { label: "Lost card", text: "I lost my debit card." },
  { label: "Transfer $250", text: "Transfer 250 dollars from savings to checking." },
  { label: "Recent transactions", text: "What are my last few transactions on checking?" },
  { label: "Duplicate charge", text: "I see two charges from Joe's Diner on the eighth. I'm worried I was double-billed." },
];

function makeAdapter(path: PathId): PathAdapter {
  switch (path) {
    case "A": return new LexPathAdapter();
    case "B": return new PipecatPathAdapter();
    case "C": return new NovaSonicPathAdapter();
  }
}

export function App() {
  const [activePath, setActivePath] = useState<PathId>("A");
  const [recording, setRecording] = useState(false);
  const [status, setStatus] = useState("Pick a path and tap the mic to begin.");
  const [messages, setMessages] = useState<Message[]>([]);
  const [metric, setMetric] = useState<Metric>({});
  const [inputText, setInputText] = useState("");
  const adapterRef = useRef<PathAdapter | null>(null);
  const transcriptRef = useRef<HTMLDivElement | null>(null);

  // Reset when path changes.
  useEffect(() => {
    void teardown();
    setMessages([]);
    setMetric({});
    setStatus(`Path ${activePath} — pick a scenario or tap the mic.`);
  }, [activePath]);

  // Auto-scroll transcript.
  useEffect(() => {
    transcriptRef.current?.scrollTo({ top: transcriptRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const handlers = useMemo(() => ({
    onMessage: (m: Message) => setMessages((prev) => [...prev, m]),
    onMetric: (m: Metric) => setMetric((prev) => ({ ...prev, ...m })),
    onStatus: (s: string) => setStatus(s),
    onError: (err: Error) => setStatus(`Error: ${err.message}`),
  }), []);

  const ensureAdapter = useCallback(async (): Promise<PathAdapter> => {
    if (adapterRef.current) return adapterRef.current;
    const a = makeAdapter(activePath);
    await a.start(handlers);
    adapterRef.current = a;
    return a;
  }, [activePath, handlers]);

  const teardown = useCallback(async () => {
    setRecording(false);
    try { await adapterRef.current?.stop(); } catch { /* ignore */ }
    adapterRef.current = null;
  }, []);

  const onMic = useCallback(async () => {
    if (recording) {
      await teardown();
      return;
    }
    try {
      if (activePath === "A") {
        // Path A is text-only in the web demo. Show a hint.
        await ensureAdapter();
        setStatus("Path A on web is text-based. Use a scenario chip or type below. (Phone version supports voice.)");
      } else {
        await ensureAdapter();
        setRecording(true);
      }
    } catch (err) {
      setStatus(`Couldn't start: ${(err as Error).message}`);
    }
  }, [activePath, recording, ensureAdapter, teardown]);

  const onScenario = useCallback(async (text: string) => {
    try {
      const a = await ensureAdapter();
      await a.sendText(text);
      setInputText("");
    } catch (err) {
      setStatus(`Couldn't send: ${(err as Error).message}`);
    }
  }, [ensureAdapter]);

  return (
    <div className="app">
      <header className="header">
        <div className="brand">
          <h1>Citizens Bank — Voice Demos</h1>
          <div className="subtitle">
            Three generations of voice AI, side by side. <a href="/calls" style={{ color: "var(--accent)", textDecoration: "none" }}>View live calls →</a>
          </div>
        </div>
        <div className="toggle-group" role="tablist" aria-label="Voice path">
          {PATHS.map((p) => (
            <button
              key={p.id}
              role="tab"
              data-path={p.id}
              aria-selected={p.id === activePath}
              className={`toggle-button ${p.id === activePath ? "active" : ""}`}
              onClick={() => setActivePath(p.id)}
            >
              <span className="dot" /> Path {p.id}: {p.name}
            </button>
          ))}
        </div>
      </header>

      <div className="layout">
        <section className="stage">
          <div className="chips">
            {SCENARIOS.map((s) => (
              <button key={s.label} className="chip" onClick={() => onScenario(s.text)}>
                {s.label}
              </button>
            ))}
          </div>

          <div className="mic-row">
            <button
              className={`mic-button ${recording ? "recording" : ""}`}
              onClick={onMic}
              disabled={activePath === "A" && recording === false ? false : false}
              aria-label={recording ? "Stop" : "Start"}
            >
              {recording ? "⏹" : "🎤"}
            </button>
            <div className="mic-status">{status}</div>
          </div>

          <div className="transcript" ref={transcriptRef}>
            {messages.length === 0 ? (
              <div style={{ color: "var(--fg-muted)", fontSize: 13 }}>
                <span className="path-tag" data-path={activePath}>Path {activePath}</span>
                {pathDescription(activePath)}
              </div>
            ) : (
              messages.map((m) => (
                <div key={m.id} className={`message ${m.role}`}>
                  <div className="role">{m.role === "tool" ? `tool · ${m.toolName ?? ""}` : m.role}</div>
                  <div className="content">{m.content}</div>
                </div>
              ))
            )}
          </div>

          {activePath === "A" && (
            <form
              onSubmit={(e) => { e.preventDefault(); if (inputText.trim()) void onScenario(inputText.trim()); }}
              style={{ display: "flex", gap: 8 }}
            >
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Type a message for the Lex bot..."
                style={{
                  flex: 1, padding: "10px 12px", background: "var(--bg)",
                  border: "1px solid var(--border)", borderRadius: 8, color: "var(--fg)", fontSize: 14,
                }}
              />
              <button type="submit" style={{
                padding: "10px 18px", background: "var(--accent)", color: "#001a26",
                borderRadius: 8, fontWeight: 600,
              }}>Send</button>
            </form>
          )}
        </section>

        <aside className="scoreboard">
          <h3>Scoreboard</h3>
          <div className="metric">
            <div className="label">Last turn latency</div>
            <div className="value">{metric.latencyMs ?? "—"}<span className="unit">ms</span></div>
          </div>
          <div className="metric">
            <div className="label">Tool calls</div>
            <div className="value">{metric.toolCalls ?? 0}</div>
          </div>
          <div className="metric">
            <div className="label">Fallbacks</div>
            <div className="value">{metric.fallbacks ?? 0}</div>
          </div>
          <div className="phone-banner">
            📞 Phone version coming in Phase 6. Three numbers — one per path.
          </div>
        </aside>
      </div>

      <footer className="footer">
        Path A: Amazon Lex V2 · Path B: Pipecat (Deepgram + Claude Sonnet 4.6 + Cartesia) · Path C: Amazon Nova 2 Sonic · all backed by the same DynamoDB-backed Banking API.
      </footer>
    </div>
  );
}

function pathDescription(p: PathId): string {
  switch (p) {
    case "A":
      return " Assisted NLU — intent-based, slot-filling, deterministic. Type a question or pick a scenario above; the Lex bot will identify the intent, fill any required slots, and respond. (Web demo uses text; voice version is on phone.)";
    case "B":
      return " Cascaded LLM — your speech is transcribed by Deepgram Nova-3, reasoned over by Claude Sonnet 4.6 with tool use, and spoken back by Cartesia Sonic-2. Tap the mic to begin a voice conversation.";
    case "C":
      return " Native speech-to-speech — Amazon Nova 2 Sonic hears you directly and speaks back directly. No separate STT or TTS in the loop. Tap the mic to begin.";
  }
}
